import {useTranslation} from "react-i18next";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import type {InsightTargetPeriodV4, InsightTileV4} from "../../models/crm-v4";
import type {StatCardData} from "../../kit/StatCard";
import {StatGrid} from "../../kit/StatGrid";
import type {InsightsLoading} from "../../kit/insightsLoading";
import {
    formatDeltaMagnitude,
    formatStatValue,
    reasonMessage,
    unitLabel,
} from "../base/insightStatCardFormatting";
import {OVERVIEW_STAT_METRICS} from "./overviewStatMetrics";
import {insightCurrency} from "../base/insightCurrency";
import type {InsightTargetRange} from "../targets/targetResolution";
import {resolveCardTarget} from "../targets/targetResolution";
import {TARGET_METRICS_BY_ID} from "../targets/targetDefinitions";

interface OverviewStatCardsProps {
    tiles?: InsightTileV4[];
    loading?: InsightsLoading;
    targetLoading?: InsightsLoading;
    targetPeriod?: InsightTargetPeriodV4;
    targetRange: InsightTargetRange;
    comparing?: boolean;
    /** The rail's search, when one is open: a card whose title does not match is not rendered. */
    matches?: (title: string, key: string) => boolean;
}

export function OverviewStatCards({
    tiles,
    loading,
    targetLoading,
    targetPeriod,
    targetRange,
    comparing = false,
    matches,
}: OverviewStatCardsProps) {
    const {t, i18n} = useTranslation();
    const currency = insightCurrency(useOrganization()?.currencyCode);
    const locale = i18n.resolvedLanguage || i18n.language;

    const items: StatCardData[] = OVERVIEW_STAT_METRICS
        .filter(definition => matches == null || matches(t(definition.title), definition.id))
        .map(definition => ({definition, tile: tiles?.find(item => item.key === definition.id)}))
        .filter(({tile}) => tile?.reason !== "moduleNotActive")
        .map(({definition, tile}) => {
            const targetDefinition = TARGET_METRICS_BY_ID.get(definition.id);
            const target = targetDefinition
                ? resolveCardTarget(targetPeriod, targetDefinition, targetRange, tile?.value)
                : undefined;
            const unavailable = tile ? reasonMessage(tile.reason, tile.coverage) : undefined;
            const unit = unitLabel(tile?.unit);
            const formatValue = (value: number | null | undefined) => {
                if (!tile) return "—";

                const formatted = formatStatValue(tile.format, value, definition.fractionDigits, currency, locale);
                return value != null && unit ? `${formatted} ${t(unit)}` : formatted;
            };
            const comparison = tile?.comparison;

            return {
                id: definition.id,
                title: t(definition.title),
                hint: unavailable ? `${t(definition.hint)} — ${t(unavailable)}` : t(definition.hint),
                value: formatValue(tile?.value),
                comparison: comparison ? {
                    // The API judges each metric's direction, so the chip does not re-derive it.
                    tone: comparison.tone,
                    trend: comparison.trend ?? "neutral",
                    change: formatDeltaMagnitude(comparison.delta, comparison.kind),
                    previous: formatValue(tile?.compareValue ?? null),
                } : comparing ? {
                    // Comparing against a period with nothing in it still earns a chip: a card with no
                    // chip at all reads as "not comparing", and the panel is where the blank is explained.
                    trend: "neutral" as const,
                    previous: formatValue(tile?.compareValue ?? null),
                } : undefined,
                target: target ? {
                    label: `${t("Budget")} ${formatValue(target.value)}`,
                    progress: target.progress,
                    achieved: target.achieved,
                } : undefined,
            };
        });

    return <StatGrid items={items} loading={loading} targetLoading={targetLoading}/>;
}

export default OverviewStatCards;
